import { localStorages } from 'investira.react.lib';
import { normalizeComparadorFundosIds } from './comparadorFundos.helpers';

export const COMPARADOR_FUNDOS_HISTORY_STORAGE_KEY = 'investira.biz:comparador-fundos:history:v1';
export const COMPARADOR_FUNDOS_HISTORY_VERSION = 1;
export const COMPARADOR_FUNDOS_HISTORY_MAX_ITEMS = 50;

/** Monta a chave que identifica uma combinação independentemente da ordem. */
function pvBuildHistoryKey(pFundosIds = []) {
  return [...pFundosIds].sort((pFirst, pSecond) => Number(pFirst) - Number(pSecond)).join('-');
}

/** Converte um valor opcional em texto persistível. */
function pvStringOrNull(pValue) {
  if (pValue === null || typeof pValue === 'undefined' || pValue === '') {
    return null;
  }

  return String(pValue);
}

/** Normaliza um registro persistido antes de entregá-lo para a interface. */
function pvNormalizeHistoryItem(pItem = {}) {
  if (!Array.isArray(pItem.fundos)) {
    return null;
  }

  const xFundos = pItem.fundos.reduce((pResult, pFundo) => {
    const xFundoId = String(pFundo?.ifeed_ativo_id || '').trim();

    if (!xFundoId || pResult.some(pItemFundo => pItemFundo.ifeed_ativo_id === xFundoId)) {
      return pResult;
    }

    return [
      ...pResult,
      {
        ifeed_ativo_id: xFundoId,
        mercado: pvStringOrNull(pFundo?.mercado),
        ativo: pvStringOrNull(pFundo?.ativo || pFundo?.descricao),
        ativo_seuid: pvStringOrNull(pFundo?.ativo_seuid),
        ticker: pvStringOrNull(pFundo?.ticker)
      }
    ];
  }, []);
  const xFundosIds = normalizeComparadorFundosIds(xFundos.map(pFundo => pFundo.ifeed_ativo_id));
  const xSavedAt = new Date(pItem.savedAt);

  if (xFundosIds.length < 2 || Number.isNaN(xSavedAt.getTime())) {
    return null;
  }

  return {
    key: pvBuildHistoryKey(xFundosIds),
    fundos: xFundosIds.map(pFundoId => xFundos.find(pFundo => pFundo.ifeed_ativo_id === pFundoId)),
    savedAt: xSavedAt.toISOString()
  };
}

/** Lê e saneia o histórico persistido no navegador. */
export function readComparadorFundosHistory(pStorage = localStorages) {
  const xPayload = pStorage.getItem(COMPARADOR_FUNDOS_HISTORY_STORAGE_KEY);

  if (xPayload?.version !== COMPARADOR_FUNDOS_HISTORY_VERSION || !Array.isArray(xPayload.items)) {
    return [];
  }

  return xPayload.items
    .map(pItem => pvNormalizeHistoryItem(pItem))
    .filter(Boolean)
    .sort((pFirst, pSecond) => new Date(pSecond.savedAt) - new Date(pFirst.savedAt))
    .filter((pItem, pIndex, pItems) => {
      return pItems.findIndex(pCandidate => pCandidate.key === pItem.key) === pIndex;
    })
    .slice(0, COMPARADOR_FUNDOS_HISTORY_MAX_ITEMS);
}

/** Inclui ou atualiza uma comparação, mantendo as mais recentes primeiro. */
export function saveComparadorFundosHistoryItem(
  pItems = [],
  pFundos = [],
  pSavedAt = new Date().toISOString()
) {
  const xItem = pvNormalizeHistoryItem({ fundos: pFundos, savedAt: pSavedAt });

  if (!xItem) {
    return pItems;
  }

  return [xItem, ...pItems.filter(pItem => pItem.key !== xItem.key)].slice(
    0,
    COMPARADOR_FUNDOS_HISTORY_MAX_ITEMS
  );
}

/** Exclui do histórico somente as combinações informadas. */
export function deleteComparadorFundosHistoryItems(pItems = [], pKeys = []) {
  const xKeys = new Set(pKeys);

  return pItems.filter(pItem => !xKeys.has(pItem.key));
}

/** Persiste o snapshot completo quando o armazenamento está disponível. */
export function writeComparadorFundosHistory(pItems = [], pStorage = localStorages) {
  if (!pStorage.checkLocalStorageExists()) {
    return false;
  }

  pStorage.setItem(COMPARADOR_FUNDOS_HISTORY_STORAGE_KEY, {
    version: COMPARADOR_FUNDOS_HISTORY_VERSION,
    items: pItems
  });

  return true;
}
